'use client'
import dynamic from 'next/dynamic'
import type { Settings } from 'react-slick'
import '@/styles/vendors/slickCarousel'
import { usePathname } from 'next/navigation'
import { PrismicNextImage } from '@prismicio/next'
import { PrismicRichText } from '@prismicio/react'
import { reviewSliderSettingsWithDot, reviewSliderSettingsWithoutDot } from '@/utils/sliderSettings'

// Lazy load components to reduce initial bundle size
const Slider = dynamic<Settings>(() => import('react-slick'), {
  ssr: false,
  loading: () => <div className="h-64 animate-pulse rounded-md bg-gray-100" />,
})
const HubSpotForm = dynamic(() => import('../common/HubSpotForm'))

type DataObject = Record<string, any>
type ItemObject = Record<string, any>

const showSliderSettingsWithDot = [
  'compliance-automation-buyers-guide-for-msps',
  'ultimate-guide-federal-frameworks-for-msps',
  'cmmc-guide-for-msps',
  'ultimate-soc-2-guide-for-msps',
  'ultimate-hipaa-guide-for-msps',
  'ultimate-iso-27001-guide-for-msps',
]

const MSPDetails = ({ data }: { data: DataObject }) => {
  const fullPathname = usePathname()
  const pathname = fullPathname.split('/msp-resources/')

  const showSliderSettings = showSliderSettingsWithDot.includes(pathname[1])
    ? reviewSliderSettingsWithDot
    : reviewSliderSettingsWithoutDot

  const metaData = data.page_title
    ? {
        title: data.page_title[0]['text'],
        srcPath: `https://secureframe.com${fullPathname}`,
      }
    : {}

  return (
    <section className="relative z-[1] pb-20 pt-8 lg:pb-[120px] lg:pt-10">
      <div className="container">
        <div className="mx-auto flex max-w-5xl flex-col items-start md:flex-row">
          <div className="w-full pr-8 md:w-2/4 lg:w-[40%]">
            <Slider
              {...showSliderSettings}
              className="[&_.slick-dots_li.slick-active_button::before]:text-primary-green-85 [&_.slick-dots_li]:m-0 [&_.slick-dots_li]:p-0 [&_.slick-dots_li]:before:content-none [&_.slick-dots_li_button::before]:text-[10px] [&_.slick-dots_li_button::before]:text-primary-ink-10 [&_.slick-dots_li_button::before]:opacity-100">
              {data.msp_slider_images.map((ebookSliderImg: ItemObject, index: number) => {
                const key = `i-${index}`
                return (
                  <div key={key}>
                    <div className="thumb">
                      <PrismicNextImage field={ebookSliderImg.slider_image} width="480" height="673" fallbackAlt="" />
                    </div>
                  </div>
                )
              })}
            </Slider>

            <div className="mt-8">
              <div className="[&>p]:mb-6 [&>p]:font-halyardDisplay [&>p]:text-xl [&>p]:tracking-[-0.45px] [&>p]:xl:text-3xl">
                <PrismicRichText field={data.sub_title} />
              </div>
              <div className="[&>ul>li]:relative [&>ul>li]:pl-8 [&>ul>li]:leading-8 [&>ul>li]:text-primary-ink-85 [&>ul>li]:before:absolute [&>ul>li]:before:left-0 [&>ul>li]:before:top-1 [&>ul>li]:before:h-5 [&>ul>li]:before:w-5 [&>ul>li]:before:bg-[url('/images/check.svg')] [&>ul>li]:before:content-['']">
                <PrismicRichText field={data.features} />
              </div>
            </div>
          </div>

          <div className="mt-14 w-full bg-secondary-gray-100 px-5 py-8 md:mt-0 md:w-2/4 lg:w-[60%] lg:px-10 lg:py-16">
            <div className="[&_#hubspot-form_.hs-richtext_a]:!text-primary-green-150 [&_#hubspot-form_.hs_company]:mb-4 [&_#hubspot-form_.hs_company]:lg:mb-6 [&_#hubspot-form_.hs_companysize]:mb-4 [&_#hubspot-form_.hs_companysize]:lg:mb-6 [&_#hubspot-form_.hs_country_dropdown]:mb-4 [&_#hubspot-form_.hs_country_dropdown]:lg:mb-6 [&_#hubspot-form_.hs_email]:mb-4 [&_#hubspot-form_.hs_email]:lg:mb-6 [&_#hubspot-form_.hs_firstname]:mb-4 [&_#hubspot-form_.hs_firstname]:lg:mb-6 [&_#hubspot-form_.hs_jobtitle]:mb-4 [&_#hubspot-form_.hs_jobtitle]:lg:mb-6 [&_#hubspot-form_.hs_lastname]:mb-4 [&_#hubspot-form_.hs_lastname]:lg:mb-6">
              <HubSpotForm
                type="ebookEmbeddedForm"
                portalId={data.hubspot_form_portal_id}
                formId={data.hubspot_form_form_id}
                sfdcCampaignId={data.hubspot_form_sfdc_campaign_id}
                metaData={metaData}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default MSPDetails
